import React, { useState, useEffect } from 'react';
import { FileText, Calendar, CheckCircle, Clock, AlertTriangle, Download, Filter, Search, Plus } from 'lucide-react';
import { ContractFilters } from '../components/contracts/ContractFilters';
import { ContractTable } from '../components/contracts/ContractTable';
import { AddContractModal } from '../components/contracts/AddContractModal';
import { useContractStore } from '../stores/contractStore';
import { toast } from 'react-hot-toast';

export function Contracts() {
  const { contracts, loading, error, fetchContracts, setFilters, exportContracts } = useContractStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  const handleSearch = (query: string) => setSearchQuery(query);

  const handleFilterChange = (filterId: string, value: string) => {
    setFilters({ [filterId]: value });
  };

  const handleExport = async (format: 'csv' | 'pdf') => {
    try {
      await exportContracts(format);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export contracts');
    }
  };

  const stats = [
    {
      title: 'Active Contracts',
      value: '0',
      change: '+12.5%',
      description: 'Current active contracts',
      icon: FileText,
    },
    {
      title: 'Pending Reviews',
      value: '0',
      change: '-2.3%',
      description: 'Awaiting review',
      icon: Clock,
    },
    {
      title: 'Total Value',
      value: '$0.0',
      change: '+15.3%',
      description: 'Combined contract value',
      icon: CheckCircle,
    },
    {
      title: 'Compliance Alerts',
      value: '2',
      change: '-1',
      description: 'Require attention',
      icon: AlertTriangle,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Hero Header */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-purple-600 to-purple-900 px-6 py-16">
        <div className="absolute inset-0 bg-grid-white/[0.1] bg-[size:16px_16px]" />
        <div className="absolute inset-0 bg-gradient-to-t from-purple-900/50 to-transparent" />
        
        <div className="relative">
          <div className="flex flex-col space-y-2">
            <h1 className="text-2xl font-bold text-white sm:text-3xl">Contract Opportunities</h1>
            <p className="text-purple-100">Browse and manage available government contracts</p>
          </div>

          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {stats.map((stat) => {
              const Icon = stat.icon;
              return (
                <div
                  key={stat.title}
                  className="group relative overflow-hidden rounded-lg bg-white/10 p-6 backdrop-blur-sm transition-all hover:bg-white/20"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-purple-200">{stat.title}</p>
                      <p className="mt-2 flex items-baseline">
                        <span className="text-2xl font-bold text-white">{stat.value}</span>
                        <span className="ml-2 text-sm font-medium text-green-300">{stat.change}</span>
                      </p>
                      <p className="mt-1 text-sm text-purple-200">{stat.description}</p>
                    </div>
                    <div className="rounded-full bg-white/20 p-3">
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Search and Actions */}
      <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
        <div className="relative flex-1 max-w-lg">
          <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search contracts..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => handleExport('csv')}
            className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
          >
            <Download className="mr-2 h-4 w-4" />
            Export CSV
          </button>
          <button
            onClick={() => handleExport('pdf')}
            className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
          >
            <Download className="mr-2 h-4 w-4" />
            Export PDF
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-all hover:-translate-y-0.5 hover:bg-purple-700 hover:shadow-md"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Contract
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-lg bg-white p-6 shadow-sm ring-1 ring-gray-900/5">
        <ContractFilters onSearch={handleSearch} onFilterChange={handleFilterChange} />
      </div>

      {/* Error Message */}
      {error && (
        <div className="rounded-lg bg-red-50 p-4">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="rounded-lg bg-white p-6 shadow-sm ring-1 ring-gray-900/5">
        {loading ? (
          <div className="flex h-64 items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-purple-600 border-t-transparent"></div>
          </div>
        ) : contracts.length > 0 ? (
          <ContractTable contracts={contracts} />
        ) : (
          <div className="flex h-64 items-center justify-center rounded-lg border-2 border-dashed border-gray-300">
            <div className="text-center">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No contracts found</h3>
              <p className="mt-1 text-sm text-gray-500">
                Try adjusting your search or filter criteria
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Add Contract Modal */}
      <AddContractModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onContractAdded={() => {
          // Refresh contracts list
          setFilters({});
        }}
      />
    </div>
  );
}